/**
 * SyncStatus component
 * Displays sync progress and pending quote count
 */

import { AlertCircle, CheckCircle2, Cloud, CloudOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { cn } from "@/lib/utils";

interface SyncStatusProps {
  className?: string;
  showManualSync?: boolean;
}

export function SyncStatus({ className, showManualSync = true }: SyncStatusProps) {
  const { syncStatus, manualSync, isOnline } = useOfflineSync();
  const { toast } = useToast();

  const handleManualSync = async () => {
    if (!isOnline) {
      toast({
        title: "Offline",
        description: "Cannot sync while offline. Please connect to the internet.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await manualSync();

      if (result?.success) {
        toast({
          title: "Sync Complete",
          description: `${result.synced} quote${result.synced !== 1 ? "s" : ""} synced successfully.`,
        });
      } else if (result?.failed > 0) {
        toast({
          title: "Sync Partially Failed",
          description: `${result.synced} synced, ${result.failed} failed.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Sync Error",
        description: error instanceof Error ? error.message : "Failed to sync quotes",
        variant: "destructive",
      });
    }
  };

  const formatLastSync = (date: Date | null) => {
    if (!date) return "Never";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getSyncStatusIcon = () => {
    if (syncStatus.isSyncing) {
      return <RefreshCw className="h-4 w-4 animate-spin" />;
    }

    if (!isOnline) {
      return <CloudOff className="h-4 w-4" />;
    }

    if (syncStatus.pendingCount > 0) {
      return <AlertCircle className="h-4 w-4" />;
    }

    if (syncStatus.lastSyncResult?.success) {
      return <CheckCircle2 className="h-4 w-4" />;
    }

    return <Cloud className="h-4 w-4" />;
  };

  const getSyncStatusColor = () => {
    if (syncStatus.isSyncing) {
      return "text-blue-600 dark:text-blue-400";
    }

    if (!isOnline) {
      return "text-gray-600 dark:text-gray-400";
    }

    if (syncStatus.pendingCount > 0) {
      return "text-amber-600 dark:text-amber-400";
    }

    return "text-green-600 dark:text-green-400";
  };

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className={cn("flex items-center gap-2", getSyncStatusColor())}>
        {getSyncStatusIcon()}
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {syncStatus.isSyncing
              ? "Syncing..."
              : syncStatus.pendingCount > 0
                ? `${syncStatus.pendingCount} pending`
                : isOnline
                  ? "Synced"
                  : "Offline"}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatLastSync(syncStatus.lastSyncTime)}
          </span>
        </div>
      </div>

      {showManualSync && isOnline && syncStatus.pendingCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleManualSync}
          disabled={syncStatus.isSyncing}
        >
          <RefreshCw className={cn("h-3 w-3 mr-1", syncStatus.isSyncing && "animate-spin")} />
          Sync Now
        </Button>
      )}
    </div>
  );
}
